import { TrenchHud } from "./trenchHud";
import { GameConstants, GasState, Layer, ObjectCategory, ZIndexes, Z_INDEX_COUNT } from "@common/constants";
import { type MapPingDefinition } from "@common/definitions/mapPings";
import { type MapData } from "@common/packets/mapPacket";
import { type MapIndicatorSerialization, type PingSerialization, type PlayerPingSerialization } from "@common/packets/updatePacket";
import { type Variation } from "@common/typings";
import { RectangleHitbox } from "@common/utils/hitbox";
import { Collision, Numeric } from "@common/utils/math";
import { waterOfSky } from "@common/utils/mapWeather";
import { FloorNames, FloorTypes, River, Terrain } from "@common/utils/terrain";
import { Vec, type Vector } from "@common/utils/vector";
import $ from "jquery";
import { Container, Graphics, RenderTexture, Sprite, Text, isMobile, type ColorSource, type Texture } from "pixi.js";
import { Color } from "pixi.js";
import { GameConsole } from "../console/gameConsole";
import { Game } from "../game";
import { GroundMaterials } from "../war/groundMaterials";
import { DIFF_LAYER_HITBOX_OPACITY, FOOTSTEP_HITBOX_LAYER, PIXI_SCALE, TEAMMATE_COLORS } from "../utils/constants";
import { TrenchioSprite, drawGroundGraphics, drawHitbox, toPixiCoords } from "../utils/pixi";
import { canvasLine } from "../utils/translations/languageLook";
import { tr } from "../utils/translations/phrases";
import { selectedLanguageCode, translate } from "../utils/translations/translations";
import { CameraManager } from "./cameraManager";
import { GasManager, GasRender } from "./gasManager";
import { InputManager } from "./inputManager";
import { SoundManager } from "./soundManager";
import { UIManager } from "./uiManager";
import type { Tween } from "../utils/tween";
import { Modes } from "@common/definitions/modes";

class MapManagerClass {
    private _expanded = false;
    get expanded(): boolean { return this._expanded; }
    set expanded(expand: boolean) {
        if (this._expanded === expand) return;

        if (this._expanded = expand) this.switchToBigMap();
        else this.switchToSmallMap();
    }

    toggle(): void { this.expanded = !this._expanded; }

    private _visible = true;
    get visible(): boolean { return this._visible; }
    set visible(visible: boolean) {
        this._visible = visible;

        this.switchToSmallMap();
        this.container.visible = visible;
        this._borderContainer.toggle(visible);
    }

    toggleMinimap(): void { this.visible = !this._visible; }

    private _position = Vec(0, 0);
    private _lastPosition = Vec(0, 0);

    // used for the gas to player line and circle
    private _gasPos = Vec(0, 0);
    private _gasRadius = 0;
    readonly safeZone = new Graphics();
    /**
     * the line from you to the next zone's middle, apart from its circle (0.98.8: drawn just before it, as the one
     * Graphics drew them: the line moves with you, the circle only when the zone does; both were drawn again, the
     * circle's every segment, at every step you took)
     */
    readonly safeLine = new Graphics();
    /** the jump-in's planes, their ways and where they are (1.06: managers/jumpIn.ts draws it each frame, in map units) */
    readonly jumpRoutes = new Graphics();

    private readonly _objectsContainer = new Container();
    readonly container = new Container();
    readonly mask = new Graphics();

    readonly sprite = new Sprite();
    private _texture?: Texture;

    indicator!: TrenchioSprite;

    readonly teammateIndicators = new Map<number, TrenchioSprite>();
    readonly teammateIndicatorContainer = new Container();

    private _width = 0;
    get width(): number { return this._width; }

    private _height = 0;
    get height(): number { return this._height; }

    private _minimapWidth = 0;
    get minimapWidth(): number { return this._minimapWidth; }

    private _minimapHeight = 0;
    get minimapHeight(): number { return this._minimapHeight; }

    margins = Vec(0, 0);

    gasRender!: GasRender;
    readonly placesContainer = new Container();

    private _terrain!: Terrain;
    get terrain(): Terrain { return this._terrain; }

    readonly pings = new Set<MapPing>();
    private readonly _border = new Graphics();
    readonly pingsContainer = new Container();
    readonly pingGraphics = new Graphics();

    readonly indicators = new Map<number, { sprite: TrenchioSprite, tween: Tween<TrenchioSprite> }>();
    readonly indicatorsContainer = new Container();

    readonly terrainGraphics = new Graphics();
    /**
     * the rivers' banks and water and the sea, drawn apart from the rest of the terrain (0.98.5): a child of the
     * terrain's graphics (seen and hidden with it), hidden by the war look when its ground shader draws the water
     * itself (the ice, in the snow) and shown again when the look drops to the plain one
     */
    readonly riverGraphics = new Graphics();

    private _objects: MapData["objects"] = [];
    /** every building and obstacle from the map packet (the War Look keeps its decals clear of them) */
    get objects(): MapData["objects"] { return this._objects; }
    /** the War Look draws its own ground, so the world grid is skipped (the minimap keeps it) */
    hideGrid = false;

    /** under snow (1.05: war/warSky.ts sets it as the map comes): the buildings' ground decorations are snowed over */
    snowGround = false;

    /**
     * a building's ground decoration's colour as the map draws it (a patch of sand, a road, a yard): under snow most of
     * the way to the snow's white, its pattern still showing
     */
    groundColor(color: ColorSource): ColorSource {
        if (!this.snowGround) return color;
        const [r, g, b] = new Color(color).toArray();
        const snow = [0.835, 0.859, 0.878];
        const mix = (from: number, to: number): number => from * 0.3 + to * 0.7;
        return new Color([mix(r, snow[0]), mix(g, snow[1]), mix(b, snow[2])]);
    }
    /** the map seed from the map packet */
    seed = 0;
    private _places: MapData["places"] = [];

    readonly debugGraphics = new Graphics();

    private _initialized = false;
    init(): void {
        if (this._initialized) {
            throw new Error("Minimap has already been instantiated");
        }
        this._initialized = true;

        this.gasRender = new GasRender(1);
        this._terrain = new Terrain(0, 0, 0, 0, 0, []);

        this._objectsContainer.mask = this.mask;

        this.container.addChild(this._objectsContainer);
        this.container.addChild(this._border);

        this.safeZone.zIndex = 997;
        this.safeLine.zIndex = 997;
        this.jumpRoutes.zIndex = 996;
        this.indicatorsContainer.zIndex = 997.5;
        this.pingsContainer.zIndex = 998;
        this.teammateIndicatorContainer.zIndex = 999;

        this.indicator = new TrenchioSprite()
            .setTint(TEAMMATE_COLORS[0])
            .setZIndex(1000);

        this._objectsContainer.addChild(
            this.sprite,
            this.placesContainer,
            this.gasRender.graphics,
            this.jumpRoutes,
            this.safeLine,
            this.safeZone,
            this.pingGraphics,
            this.pingsContainer,
            this.indicatorsContainer,
            this.indicator,
            this.teammateIndicatorContainer
        ).sortChildren();

        if (InputManager.isMobile) {
            this._borderContainer.on("pointerup", e => {
                this.switchToBigMap();
                e.stopImmediatePropagation();
            });
        }

        $("#btn-close-minimap").on("pointerdown", e => {
            this.switchToSmallMap();
            e.stopImmediatePropagation();
        });

        if (DEBUG_CLIENT) {
            GameConsole.variables.addChangeListener("db_show_hitboxes", val => {
                if (!Game.gameStarted) return;
                val = val && GameConsole.getBuiltInCVar("db_show_hitboxes_terrain");
                if (val) {
                    this.renderMapDebug();
                }
                this.debugGraphics.visible = val;
            });
            GameConsole.variables.addChangeListener("db_show_hitboxes_terrain", val => {
                if (!Game.gameStarted) return;
                val = val && GameConsole.getBuiltInCVar("db_show_hitboxes");
                if (val) {
                    this.renderMapDebug();
                }
                this.debugGraphics.visible = val;
            });
        }
    }

    isInOcean(position: Vector): boolean {
        return !this.terrain.beachHitbox.isPointInside(position);
    }

    distanceToShoreSquared(position: Vector): number {
        // (0.98.8: the last answer kept for the place it was for: the ambience asks every tick, the shore's every edge
        // each time, and a player standing still asked the same thing over and over)
        const points = this.terrain.beachHitbox.points;
        const last = this._shore;
        if (last.points === points && last.x === position.x && last.y === position.y) return last.distance;
        const distance = Collision.distToPolygonSq(position, points);
        last.points = points; last.x = position.x; last.y = position.y; last.distance = distance;
        return distance;
    }

    /** the shore's distance last worked out (0.98.8): for which shore, from where */
    private readonly _shore: { points?: readonly Vector[], x: number, y: number, distance: number } = { x: Number.NaN, y: Number.NaN, distance: 0 };

    drawTerrain(ctx: Graphics, scale: number, gridLineWidth: number, rivers: Graphics = ctx): void {
        ctx.zIndex = ZIndexes.Ground;

        const radius = 20 * scale;
        const [
            { points: beachPoints },
            { points: grassPoints }
        ] = [this._terrain.beachHitbox, this._terrain.grassHitbox];

        const colors = Game.colors;

        const beach = scale === 1 ? beachPoints : beachPoints.map(point => Vec.scale(point, scale));
        // The grass is a hole in the map shape, the background clear color is the grass color
        ctx.roundShape(beach, radius);
        ctx.cut();

        ctx.roundShape?.(beach, radius);
        ctx.fill(colors.beach);

        const grass = scale === 1 ? grassPoints : grassPoints.map(point => Vec.scale(point, scale));
        ctx.roundShape(grass, radius);
        ctx.cut();

        for (const building of this._objects) {
            if (!building.isBuilding) continue;

            const definition = building.definition;
            for (const ground of definition.terrainGraphics ?? []) {
                const hitbox = ground.hitbox.transform(building.position, 1, building.orientation);
                // (0.98.7: a strip of sand in its material on the world's map; the water and the minimap plain)
                if (scale === PIXI_SCALE && !ground.waterTint) {
                    GroundMaterials.fill(ctx, hitbox, this.groundColor(ground.color), scale);
                    continue;
                }
                ctx.beginPath();
                drawGroundGraphics(hitbox, ctx, scale);
                ctx.closePath();
                ctx.fill(ground.waterTint ? Game.mode.colors.water : this.groundColor(ground.color));
            }
        }

        // gets the river polygon with the middle 2 points not rounded
        // so it joins nicely with other rivers
        function getRiverPoly(points: readonly Vector[]): Array<Vector & { readonly radius: number }> {
            const half = points.length / 2;
            return points.map(
                (point, index) => ({
                    x: point.x * scale,
                    y: point.y * scale,
                    radius: (index === half || index === half - 1) ? 0 : radius
                })
            );
        }

        // river bank needs to be drawn first
        for (const river of this._terrain.rivers) {
            rivers
                .beginPath()
                .roundShape(getRiverPoly(river.bankHitbox.points), 0, true)
                .fill(river.isTrail ? colors.trail : colors.riverBank);
        }

        rivers.beginPath();
        for (const river of this._terrain.rivers) {
            if (river.waterHitbox) {
                rivers.roundShape(getRiverPoly(river.waterHitbox.points), 0, true);
            }
        }
        rivers.fill(colors.water);

        rivers.beginPath();
        rivers.rect(0, 0, this._width * scale, this._height * scale);
        rivers.fill(colors.water);
        rivers.roundShape(beach, radius);
        rivers.cut();
        rivers.beginPath();

        // (the grid with the rivers: over the water, as it was drawn)
        if (!this.hideGrid || scale === 1) {
            rivers.setStrokeStyle({
                color: 0x000000,
                alpha: 0.1,
                width: gridLineWidth
            });

            const gridSize = GameConstants.gridSize * scale;
            const gridWidth = this._width * scale;
            const gridHeight = this._height * scale;
            for (let x = 0; x <= gridWidth; x += gridSize) {
                rivers.moveTo(x, 0);
                rivers.lineTo(x, gridHeight);
            }

            for (let y = 0; y <= gridHeight; y += gridSize) {
                rivers.moveTo(0, y);
                rivers.lineTo(gridWidth, y);
            }

            rivers.stroke();
            rivers.beginPath();
        }
        ctx.beginPath();

        // (the buildings' ground decorations with the rivers too, over them as they were drawn; the war look draws its
        // own copy of them over its shader's ground, so hiding the rivers under its ice loses none)
        for (const building of this._objects) {
            if (!building.isBuilding) continue;

            const definition = building.definition;
            for (const ground of definition.groundGraphics ?? []) {
                const hitbox = ground.hitbox.transform(building.position, 1, building.orientation);
                // (0.98.7: on the world's map in its material; the minimap keeps the plain colour)
                if (scale === PIXI_SCALE) {
                    GroundMaterials.fill(rivers, hitbox, this.groundColor(ground.color), scale);
                    continue;
                }
                rivers.beginPath();
                drawGroundGraphics(hitbox, rivers, scale);
                rivers.closePath();
                rivers.fill(this.groundColor(ground.color));
            }
        }

        if (DEBUG_CLIENT) {
            if (GameConsole.getBuiltInCVar("db_show_hitboxes") && GameConsole.getBuiltInCVar("db_show_hitboxes_terrain")) {
                this.renderMapDebug();
                this.debugGraphics.visible = true;
            }
        }
    }

    /**
     * The terrain as material weights for the War Look ground shader:
     * beach (1,0,0) water (0,1,0) road/trail (0,0,1) river bank (0.5,0,0.5), mud (grass) is left black.
     */
    drawTerrainIds(ctx: Graphics, scale: number): void {
        const BEACH = 0xff0000, WATER = 0x00ff00, TRAIL = 0x0000ff, BANK = 0x800080, MUD = 0x000000;
        const radius = 20 * scale;
        const beach = this._terrain.beachHitbox.points.map(point => Vec.scale(point, scale));
        const grass = this._terrain.grassHitbox.points.map(point => Vec.scale(point, scale));
        ctx.rect(0, 0, this._width * scale, this._height * scale).fill(WATER);
        ctx.roundShape(beach, radius).fill(BEACH);
        ctx.roundShape(grass, radius).fill(MUD);
        const grassColor = Game.colors.grass.toNumber();
        for (const building of this._objects) {
            if (!building.isBuilding) continue;
            for (const ground of building.definition.terrainGraphics ?? []) {
                ctx.beginPath();
                drawGroundGraphics(ground.hitbox.transform(building.position, 1, building.orientation), ctx, scale);
                ctx.closePath();
                const color = ground.waterTint ? WATER : new Color(ground.color).toNumber() === grassColor ? MUD : BEACH;
                ctx.fill(color);
            }
        }
        const riverPoly = (points: readonly Vector[]): Array<Vector & { readonly radius: number }> => {
            const half = points.length / 2;
            return points.map((point, index) => ({ x: point.x * scale, y: point.y * scale, radius: (index === half || index === half - 1) ? 0 : radius }));
        };
        for (const river of this._terrain.rivers) {
            ctx.beginPath().roundShape(riverPoly(river.bankHitbox.points), 0, true).fill(river.isTrail ? TRAIL : BANK);
        }
        for (const river of this._terrain.rivers) {
            if (river.waterHitbox) ctx.beginPath().roundShape(riverPoly(river.waterHitbox.points), 0, true).fill(WATER);
        }
    }

    /**
     * only the buildings' own ground decorations (the War Look ground replaces the rest of the terrain): each in its
     * material (0.98.7, war/groundMaterials.ts: a lot's concrete, a yard's dirt), or `normal`, their normal maps
     */
    drawBuildingGround(ctx: Graphics, normal = false): void {
        ctx.zIndex = ZIndexes.Ground;
        for (const building of this._objects) {
            if (!building.isBuilding) continue;
            for (const ground of building.definition.groundGraphics ?? []) {
                GroundMaterials.fill(ctx, ground.hitbox.transform(building.position, 1, building.orientation), this.groundColor(ground.color), PIXI_SCALE, normal);
            }
        }
    }

    async renderMap(): Promise<void> {
        // Draw the terrain graphics
        const terrainGraphics = this.terrainGraphics;
        terrainGraphics.clear();
        const riverGraphics = this.riverGraphics;
        riverGraphics.clear();
        if (riverGraphics.parent !== terrainGraphics) terrainGraphics.addChild(riverGraphics);
        const mapGraphics = new Graphics();

        const colors = Game.colors;

        this.drawTerrain(terrainGraphics, PIXI_SCALE, 6, riverGraphics);
        this.drawTerrain(mapGraphics, 1, 2);

        // drawn map borders
        // since the pixi clear color is the grass color for performance reasons
        // big rectangles need to be draw as the map border
        const margin = 5120;
        const doubleMargin = margin * 2;

        const realWidth = this._width * PIXI_SCALE;
        const realHeight = this._height * PIXI_SCALE;

        terrainGraphics.rect(-margin, -margin, realWidth + doubleMargin, margin);
        terrainGraphics.rect(-margin, realHeight, realWidth + doubleMargin, margin);
        terrainGraphics.rect(-margin, -margin, margin, realHeight + doubleMargin);
        terrainGraphics.rect(realWidth, -margin, margin, realHeight + doubleMargin);
        terrainGraphics.fill(colors.border);

        CameraManager.addObject(terrainGraphics);
        terrainGraphics.zIndex = -5;

        // Draw the minimap objects
        const mapRender = new Container();
        mapRender.addChild(mapGraphics);

        for (const mapObject of this._objects) {
            const zIndexOffset = ((mapObject.layer ?? Layer.Ground) + 1) * Z_INDEX_COUNT;
            switch (mapObject.type) {
                case ObjectCategory.Obstacle: {
                    const {
                        definition,
                        position,
                        rotation,
                        scale: initialScale
                    } = mapObject;

                    const variation = definition.variations !== undefined
                        ? (mapObject.id % definition.variations) as Variation
                        : undefined;

                    const scale = (initialScale ?? 1) / PIXI_SCALE;
                    const zIndex = (definition.zIndex ?? ZIndexes.ObstaclesLayer1) + zIndexOffset;

                    let texture = definition.frames?.base ?? definition.idString;

                    if (variation !== undefined && !(definition.isTree && !definition.trunkVariations)) {
                        // biome-ignore lint/style/noNonNullAssertion: we should probably find a better way
                        texture += `_${definition.isTree && definition.leavesVariations ? Math.ceil((variation + 1) / definition.trunkVariations!) : variation + 1}`;
                    }

                    const image = new TrenchioSprite(texture)
                        .setVPos(position)
                        .setRotation(rotation)
                        .setScale(scale)
                        .setZIndex(zIndex);
                    if (definition.tint !== undefined) image.setTint(definition.tint);

                    if (definition.isTree && definition.frames?.leaves) {
                        const leavesSprite = new TrenchioSprite(`${definition.frames.leaves}${variation !== undefined ? `_${(definition.leavesVariations ? variation % definition.leavesVariations : variation) + 1}` : ""}`)
                            .setVPos(position)
                            .setRotation(rotation)
                            .setScale(scale)
                            .setZIndex(zIndex + 0.1);
                        mapRender.addChild(leavesSprite);
                    }

                    mapRender.addChild(image);
                    break;
                }

                case ObjectCategory.Building: {
                    if (mapObject.layer < Layer.Ground) continue;
                    const definition = mapObject.definition;
                    const rotation = mapObject.rotation;

                    const floorContainer = new Container({
                        sortableChildren: true,
                        zIndex: (definition.floorZIndex ?? ZIndexes.BuildingsFloor) + zIndexOffset,
                        rotation,
                        position: mapObject.position
                    });

                    for (const image of definition.floorImages ?? []) {
                        const sprite = new TrenchioSprite(image.key)
                            .setVPos(image.position)
                            .setRotation(image.rotation ?? 0)
                            .setZIndex(image.zIndex ?? 0)
                            .setTint(image.beachTinted ? Game.colors.beach : 0xffffff);

                        if (image.alpha !== undefined) sprite.setAlpha(image.alpha);
                        if (image.tint !== undefined) sprite.setTint(image.tint);
                        sprite.scale = Vec.scale(image.scale ?? Vec(1, 1), 1 / PIXI_SCALE);
                        floorContainer.addChild(sprite);
                    }
                    mapRender.addChild(floorContainer);

                    const ceilingContainer = new Container({
                        sortableChildren: true,
                        zIndex: (definition.ceilingZIndex ?? ZIndexes.BuildingsCeiling) + zIndexOffset,
                        rotation,
                        position: mapObject.position
                    });

                    for (const image of definition.ceilingImages ?? []) {
                        const sprite = new TrenchioSprite(image.key)
                            .setVPos(image.position)
                            .setRotation(image.rotation ?? 0)
                            .setZIndex(image.zIndex ?? 0);

                        sprite.scale.set(1 / PIXI_SCALE);
                        sprite.scale.x *= image.scale?.x ?? 1;
                        sprite.scale.y *= image.scale?.y ?? 1;
                        if (image.tint !== undefined) sprite.setTint(image.tint);
                        ceilingContainer.addChild(sprite);
                    }
                    mapRender.addChild(ceilingContainer);

                    if (definition.graphics?.length) {
                        const ctx = new Graphics();
                        ctx.zIndex = definition.graphicsZIndex ?? ZIndexes.BuildingsFloor;
                        for (const graphics of definition.graphics) {
                            ctx.beginPath();
                            drawGroundGraphics(graphics.hitbox.transform(mapObject.position, 1, mapObject.orientation), ctx, 1);
                            ctx.closePath();
                            ctx.fill(graphics.color);
                        }
                        mapRender.addChild(ctx);
                    }
                    break;
                }
            }
        }

        mapRender.sortChildren();

        // Render all obstacles and buildings to a texture
        this._texture?.destroy(true);
        this._texture = RenderTexture.create({
            width: this._width,
            height: this._height,
            resolution: isMobile.any ? 1 : 2,
            antialias: GameConsole.getBuiltInCVar("cv_antialias")
        });

        Game.pixi.renderer.render({ container: mapRender, target: this._texture, clearColor: colors.grass });
        this.sprite.texture.destroy(true);
        this.sprite.texture = this._texture;
        mapRender.destroy({
            children: true,
            texture: false
        });

        // Wait for font to load
        await Game.fontObserver;

        // Add the places (the last map's names gone for good: their pictures were kept, match after match, 1.06.1)
        for (const child of this.placesContainer.removeChildren()) child.destroy();
        for (const place of this._places) {
            const text = new Text(
                {
                    // (the map's own names, as the server sends them: in the language picked where the phrasebook has them;
                    // Arabic's right to left, 1.04.5)
                    text: canvasLine(tr(place.name), selectedLanguageCode()),
                    style: {
                        fill: "white",
                        fontFamily: "Inter",
                        fontWeight: "600",
                        stroke: {
                            color: "black",
                            width: 2
                        },
                        fontSize: 18,
                        dropShadow: {
                            alpha: 0.8,
                            color: "black",
                            blur: 2,
                            distance: 5
                        }
                    }
                }
            );
            text.alpha = 0.7;
            text.anchor.set(0.5);
            text.position = place.position;

            this.placesContainer.addChild(text);
        }
    }

    renderMapDebug(): void {
        if (!DEBUG_CLIENT) return;

        const debugGraphics = this.debugGraphics;
        debugGraphics.clear();
        debugGraphics.zIndex = 999;
        for (const [hitbox, { floorType, layer }] of this._terrain.floors) {
            drawHitbox(hitbox, (FloorTypes[floorType].debugColor * (2 ** 8) + 0x80).toString(16), debugGraphics, layer as Layer === FOOTSTEP_HITBOX_LAYER ? 1 : DIFF_LAYER_HITBOX_OPACITY);
            //                                                      ^^^^^^ using << 8 can cause 32-bit overflow lol
        }

        drawHitbox(this._terrain.beachHitbox, FloorTypes.sand.debugColor, debugGraphics);
        drawHitbox(this._terrain.grassHitbox, FloorTypes.grass.debugColor, debugGraphics);

        for (const river of this._terrain.rivers) {
            const points = river.points.map(point => Vec.scale(point, PIXI_SCALE));

            if (river.waterHitbox) drawHitbox(river.waterHitbox, FloorTypes[this._terrain.waterType].debugColor, debugGraphics);
            drawHitbox(river.bankHitbox, FloorTypes.sand.debugColor, debugGraphics);

            debugGraphics.setStrokeStyle({
                width: 10,
                color: 0
            });

            debugGraphics.beginPath();
            debugGraphics.moveTo(points[0].x, points[0].y);
            for (let i = 1; i < points.length; i++) {
                const point = points[i];
                debugGraphics.lineTo(point.x, point.y);
            }
            debugGraphics.stroke();

            for (const point of points) {
                debugGraphics.beginPath();
                debugGraphics.arc(point.x, point.y, 20, 0, Math.PI * 2);
                debugGraphics.fill(0xff0000);
            }
        }

        CameraManager.addObject(debugGraphics);
    }

    updateFromPacket(mapPacket: MapData): void {
        console.log(`Joining game with seed: ${mapPacket.seed}`);
        this.seed = mapPacket.seed;
        UIManager.ui.loaderText.text(translate("loading_joining_game"));

        const width = this._width = mapPacket.width;
        const height = this._height = mapPacket.height;
        this._objects = mapPacket.objects;
        this._places = mapPacket.places;

        const mapBounds = new RectangleHitbox(
            Vec(mapPacket.oceanSize, mapPacket.oceanSize),
            Vec(mapPacket.width - mapPacket.oceanSize, mapPacket.height - mapPacket.oceanSize)
        );

        const rivers: River[] = [];
        rivers.push(...mapPacket.rivers.map(({ width, points, isTrail, customBank }) => new River(width, points as Vector[], rivers, mapBounds, isTrail, customBank)));

        const baseMode = Modes[Game.mode.similarTo ?? Game.modeName];
        // (0.98.5: the snow's ice, as the server has it)
        const waterType = baseMode.replaceWaterBy ?? waterOfSky(mapPacket.sky);

        this._terrain = new Terrain(
            width,
            height,
            mapPacket.oceanSize,
            mapPacket.beachSize,
            mapPacket.seed,
            rivers,
            waterType
        );

        for (const object of this._objects) {
            if (object.isBuilding) {
                for (const floor of object.definition.floors ?? []) {
                    let floorType = floor.type;
                    const floorLayer = floor.layer ?? object.layer ?? 0;
                    if (floorType === FloorNames.Water && floorLayer === Layer.Ground) {
                        floorType = waterType;
                    }
                    const hitbox = floor.hitbox.transform(object.position, 1, object.orientation);
                    this._terrain.addFloor(floorType, hitbox, floorLayer);
                }
            }
        }

        void this.renderMap();
        this.resize();
    }

    update(): void {
        // Hide the map while spectating in mobile
        if (InputManager.isMobile && this._visible) {
            if (UIManager.ui.spectatingContainer.css("display") !== "none") { // class check wont work here for some reason
                this._borderContainer.hide();
                this.container.visible = false;
            } else if (!this._expanded) {
                this._borderContainer.show();
                this.container.visible = true;
            }

            UIManager.ui.gasAndDebug.toggleClass("spectating-mode", !this.container.visible);
        }

        if (this.pings.size > 0) {
            this.pingGraphics.clear();
            const now = Date.now();

            for (const ping of this.pings) {
                const radius = Numeric.lerp(0, 2048, (now - ping.startTime) / 7000);

                if (now > ping.endTime) {
                    this.pings.delete(ping);
                    ping.destroy();
                    continue;
                }

                if (radius >= 2048) continue;

                this.pingGraphics.setStrokeStyle({
                    color: ping.color,
                    width: 5,
                    cap: "round"
                });

                this.pingGraphics.arc(ping.position.x, ping.position.y, radius, 0, Math.PI * 2);
                this.pingGraphics.stroke();
            }
        }

        this.gasRender.update();
        // only re-render gas line and circle if something changed
        if (
            GasManager.state === GasState.Inactive || (
                this._position.x === this._lastPosition.x
                && this._position.y === this._lastPosition.y
                && GasManager.newRadius === this._gasRadius
                && GasManager.newPosition.x === this._gasPos.x
                && GasManager.newPosition.y === this._gasPos.y
            )
        ) return;

        const zoneMoved = GasManager.newRadius !== this._gasRadius
            || GasManager.newPosition.x !== this._gasPos.x
            || GasManager.newPosition.y !== this._gasPos.y;
        this._lastPosition = this._position;
        this._gasPos = GasManager.newPosition;
        this._gasRadius = GasManager.newRadius;

        this.safeLine.clear();

        this.safeLine.beginPath();
        this.safeLine.setStrokeStyle({
            color: 0x00f9f9,
            width: 2,
            cap: "round"
        });
        // draw line from player to gas center
        this.safeLine.moveTo(this._position.x, this._position.y)
            .lineTo(this._gasPos.x, this._gasPos.y)
            .closePath().stroke();

        // draw circle (only when the zone changed: 0.98.8)
        if (!zoneMoved && this._circleDrawn) return;
        this._circleDrawn = true;
        this.safeZone.clear();
        this.safeZone.beginPath()
            .setStrokeStyle({
                color: 0xffffff,
                width: 2,
                cap: "round"
            })
            .arc(this._gasPos.x, this._gasPos.y, this._gasRadius, 0, Math.PI * 2)
            .closePath()
            .stroke();
    }

    /** the next zone's circle is drawn for the zone as it is (0.98.8) */
    private _circleDrawn = false;

    private readonly _borderContainer = $("#minimap-border");

    resize(): void {
        if (!this._initialized) return;

        this._border.visible = this._expanded;
        const uiScale = GameConsole.getBuiltInCVar("cv_ui_scale") * TrenchHud.fitScale();

        if (Game.spectating) {
            UIManager.ui.spectatingContainer.toggle(UIManager.ui.spectatingOptions.hasClass("fa-eye-slash"));
        }

        if (this._expanded) {
            const screenWidth = window.innerWidth;
            const screenHeight = window.innerHeight;
            const smallestDim = Numeric.min(screenHeight, screenWidth);
            this.container.scale.set(smallestDim / this._height);
            this._minimapWidth = this.sprite.width * this.container.scale.x;
            this._minimapHeight = this.sprite.height * this.container.scale.y;
            this.margins = Vec(screenWidth / 2 - (this._minimapWidth / 2), screenHeight / 2 - (this._minimapHeight / 2));

            const closeButton = $("#btn-close-minimap");
            const closeButtonPos = Numeric.min(
                this.margins.x + this._minimapWidth + 16,
                screenWidth - (closeButton.outerWidth() ?? 0)
            ) / uiScale;
            closeButton.css("left", `${closeButtonPos}px`);

            this._border.clear();
            this._border.setStrokeStyle({
                width: 4,
                color: 0x00000
            });
            this._border.rect(-this.sprite.width / 2, 0, this.sprite.width, this.sprite.height);
            this._border.stroke();

            this.indicator.scale.set(1);
            for (const [, indicator] of this.teammateIndicators) {
                indicator.setScale(1);
            }
        } else {
            const bounds = this._borderContainer[0].getBoundingClientRect();
            const border = parseInt(this._borderContainer.css("border-width")) * uiScale;

            this._minimapWidth = bounds.width - border * 2;
            this._minimapHeight = bounds.height - border * 2;
            this.margins = Vec(bounds.left + border, bounds.top + border);

            if (window.innerWidth > 1200) {
                this.container.scale.set(1 / 1.25 * uiScale);
            } else {
                this.container.scale.set(1 / 2 * uiScale);
            }

            this.indicator.scale.set(0.75);
            for (const [, indicator] of this.teammateIndicators) {
                indicator.setScale(0.75);
            }
        }

        // the Trench Command HUD shows the minimap inside a round compass bezel
        if (!this._expanded && TrenchHud.active) {
            this.mask.clear()
                .circle(this.margins.x + this._minimapWidth / 2, this.margins.y + this._minimapHeight / 2, Math.min(this._minimapWidth, this._minimapHeight) / 2)
                .fill();
        } else {
            this.mask.clear()
                .rect(this.margins.x, this.margins.y, this._minimapWidth, this._minimapHeight)
                .fill();
        }

        this.updatePosition();
        this.updateTransparency();

        for (const text of this.placesContainer.children) {
            text.scale.set(1 / this.container.scale.x);
        }
    }

    setPosition(pos: Vector): void {
        this._position = Vec.clone(pos);
        this.indicator.setVPos(pos);
        this.updatePosition();
    }

    updatePosition(): void {
        if (this._expanded) {
            this.container.position.set(window.innerWidth / 2, window.innerHeight / 2 - this._minimapHeight / 2);
            this._objectsContainer.position.set(-this._width / 2, 0);
            return;
        }
        const pos = Vec.clone(this._position);
        pos.x -= (this._minimapWidth / 2 + this.margins.x) / this.container.scale.x;
        pos.y -= (this._minimapHeight / 2 + this.margins.y) / this.container.scale.y;

        this.container.position.set(0, 0);
        this._objectsContainer.position.copyFrom(Vec.scale(pos, -1));
    }

    private readonly _uiCache = Object.freeze({
        scopes: $<HTMLDivElement>("#scopes-container"),
        closeMinimap: $<HTMLButtonElement>("#btn-close-minimap"),
        killLeader: $<HTMLDivElement>("#ui-kill-leader"),
        centerBottom: $<HTMLDivElement>("#center-bottom-container"),
        killCounter: $<HTMLDivElement>("#kill-counter")
    });

    switchToBigMap(): void {
        this._expanded = true;

        const ui = UIManager.ui;
        this.container.visible = true;
        this._borderContainer.hide();

        this._uiCache.scopes.hide();
        ui.spectatingContainer.hide();
        ui.gasMsgInfo.hide();
        this._uiCache.closeMinimap.show();
        this._uiCache.killLeader.hide();
        this._uiCache.centerBottom.hide();
        this._uiCache.killCounter.show();
        ui.killFeed.hide();

        this.resize();
    }

    switchToSmallMap(): void {
        this._expanded = false;

        const ui = UIManager.ui;

        this._uiCache.closeMinimap.hide();
        this._uiCache.centerBottom.show();
        ui.gasMsgInfo.show();
        this._uiCache.scopes.show();
        ui.killFeed.show();

        if (Game.spectating) ui.spectatingContainer.show();
        const width = window.innerWidth;
        if (width > 768) this._uiCache.killLeader.show();
        this._uiCache.killCounter.hide();

        // We check again for the mobile spectating stuff due to a bug
        if (InputManager.isMobile && Game.spectating && this._visible) {
            UIManager.ui.spectatingContainer.toggleClass("mobile-visible", false);
            UIManager.ui.spectatingContainer.hide();
            this.container.visible = UIManager.ui.spectatingContainer.hasClass("mobile-visible");
        }

        if (this.visible) this._borderContainer.show();
        this.resize();
    }

    updateTransparency(): void {
        this.container.alpha = GameConsole.getBuiltInCVar(
            this._expanded
                ? "cv_map_transparency"
                : "cv_minimap_transparency"
        );
    }

    addMapPing(data: PingSerialization): void {
        const { position, definition } = data;
        const playerId = definition.isPlayerPing ? (data as PlayerPingSerialization).playerId : undefined;

        const ping = new MapPing(
            position,
            definition,
            playerId
        );

        if (definition.sound !== undefined) SoundManager.play(definition.sound);

        this.pingsContainer.addChild(ping.mapImage);
        if (ping.inGameImage) CameraManager.addObject(ping.inGameImage);

        // delete previous pings from the same player
        if (ping.definition.isPlayerPing) {
            for (const otherPing of this.pings) {
                if (
                    otherPing.definition !== ping.definition
                    || otherPing.playerId !== playerId
                ) continue;

                otherPing.destroy();
                this.pings.delete(otherPing);
            }
        }

        this.pings.add(ping);
        if (!ping.definition.ignoreExpiration) {
            Game.addTimeout(() => {
                ping.destroy();
            }, 10000);
        }
    }

    updateMapIndicator(data: MapIndicatorSerialization): void {
        const id = data.id;
        const indicator = this.indicators.get(id);
        let sprite = indicator?.sprite;
        let tween = indicator?.tween;
        if (sprite) {
            if (data.dead) {
                this.indicators.delete(id);
                tween?.kill();
                Game.addTween({
                    target: sprite,
                    to: { alpha: 0 },
                    duration: 250,
                    onComplete: () => sprite && this.indicatorsContainer.removeChild(sprite)
                });
            } else {
                if (data.position) sprite.setVPos(data.position);
                if (data.definition) sprite.setFrame(data.definition.idString);
            }
        } else if (!data.dead) {
            sprite = new TrenchioSprite(data.definition?.idString)
                .setVPos(data.position ?? Vec(0, 0))
                .setAlpha(0);
            tween = Game.addTween({
                target: sprite,
                to: { alpha: 1 },
                duration: 250
            });
            this.indicatorsContainer.addChild(sprite);
            this.indicators.set(id, { sprite, tween });
        }
    }

    reset(): void {
        this.safeZone.clear();
        this.safeLine.clear();
        this._circleDrawn = false;
        this.pingGraphics.clear();
        // (each ping's markers go too: on the map, and the one in the game world, which nothing else removed)
        for (const ping of this.pings) ping.destroy();
        this.pings.clear();
        this.pingsContainer.removeChildren();
        this.indicators.clear();
        this.indicatorsContainer.removeChildren();
        this.teammateIndicators.clear();
        this.teammateIndicatorContainer.removeChildren();
    }
}

export const MapManager = new MapManagerClass();

export class MapPing {
    readonly startTime: number;
    readonly endTime: number;
    readonly mapImage: TrenchioSprite;
    readonly inGameImage?: TrenchioSprite;
    readonly color: ColorSource;

    constructor(
        readonly position: Vector,
        readonly definition: MapPingDefinition,
        readonly playerId?: number
    ) {
        this.startTime = Date.now();
        this.endTime = this.startTime + this.definition.lifetime * 1000;

        this.color = definition.color;

        if (definition.isPlayerPing && playerId) {
            this.color = TEAMMATE_COLORS[UIManager.getTeammateColorIndex(playerId) ?? UIManager.teammates.findIndex(({ id }) => id === playerId)];
        }

        this.mapImage = new TrenchioSprite(definition.idString)
            .setVPos(position)
            .setTint(this.color)
            .setScale(0.5);

        if (this.definition.showInGame) {
            this.inGameImage = new TrenchioSprite(definition.idString)
                .setVPos(toPixiCoords(position))
                .setTint(this.color)
                .setZIndex(1001);
        }
    }

    destroy(): void {
        this.mapImage.destroy();
        this.inGameImage?.destroy();
    }
}
